import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import {
  PROPERTY_TYPES, categoryMeta, bookingModeMeta, nightsBetween, bookableOptions, orderedCategories,
  type HebergementProperty, type HebergementUnit, type HebergementCategory,
} from '@/lib/hebergement';

interface MediaRow { property_id: number | null; url: string; type: string; is_cover: boolean; sort_order: number }

export default function HebergementsPage() {
  const { tenant, isTenant } = useTenant();

  const [properties, setProperties] = useState<HebergementProperty[]>([]);
  const [units, setUnits] = useState<HebergementUnit[]>([]);
  const [categories, setCategories] = useState<HebergementCategory[]>([]);
  const [coverByProp, setCoverByProp] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [checking, setChecking] = useState(false);
  const [availableIds, setAvailableIds] = useState<Set<number> | null>(null);

  // Filtres
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [guests, setGuests] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');

  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  useEffect(() => {
    if (!isTenant || !tenant) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [propRes, unitRes, mediaRes, catRes] = await Promise.all([
          supabase
            .from('hebergement_properties')
            .select('*')
            .eq('merchant_id', tenant.id)
            .eq('published', true)
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
          supabase.from('hebergement_units').select('*').eq('merchant_id', tenant.id).eq('published', true),
          supabase
            .from('hebergement_media')
            .select('property_id, url, type, is_cover, sort_order')
            .eq('merchant_id', tenant.id)
            .order('sort_order', { ascending: true }),
          supabase.from('typehebergement').select('*').order('sort_order', { ascending: true }),
        ]);
        if (propRes.error) throw propRes.error;
        if (unitRes.error) throw unitRes.error;
        if (mediaRes.error) throw mediaRes.error;
        if (cancelled) return;

        const list = (propRes.data || []) as HebergementProperty[];
        setProperties(list);
        setUnits((unitRes.data || []) as HebergementUnit[]);
        setCategories(
          ((catRes.data || []) as HebergementCategory[]).filter(
            (c) => c.idcommerce == null || Number(c.idcommerce) === Number(tenant.id),
          ),
        );

        const covers: Record<number, string> = {};
        for (const m of (mediaRes.data || []) as MediaRow[]) {
          if (m.property_id == null || m.type === 'video') continue;
          const existing = covers[m.property_id];
          if (!existing || m.is_cover) covers[m.property_id] = m.url;
        }
        setCoverByProp(covers);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur de chargement des hébergements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, tenant]);

  const typeOptions: { value: string; label: string; icon: string; isChild: boolean }[] =
    categories.length > 0
      ? orderedCategories(categories).map(({ cat, isChild }) => ({
          value: cat.slug,
          label: cat.nom,
          icon: cat.icon || 'ri-building-4-line',
          isChild,
        }))
      : PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label, icon: t.icon, isChild: false }));

  // Filtrage de base (texte, type, capacité)
  const baseFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const g = parseInt(guests, 10) || 0;
    return properties.filter((p) => {
      if (type && p.type !== type) return false;
      if (g > 0 && (p.capacity || 0) < g) return false;
      if (q) {
        const hay = [p.name, p.city, p.region, p.country, p.description].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [properties, query, type, guests]);

  // Vérification de disponibilité réelle (via le moteur serveur) quand des dates sont choisies.
  useEffect(() => {
    const datesValid = checkIn && checkOut && checkOut > checkIn;
    if (!datesValid || baseFiltered.length === 0) {
      setAvailableIds(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    (async () => {
      const available = new Set<number>();
      for (const p of baseFiltered) {
        const opts = bookableOptions(p, units.filter((u) => u.property_id === p.id));
        let ok = false;
        for (const opt of opts) {
          try {
            const { data } = await supabase.rpc('hebergement_check_availability', {
              p_property_id: p.id,
              p_unit_id: opt.unitId,
              p_check_in: checkIn,
              p_check_out: checkOut,
            });
            if (data === true) { ok = true; break; }
          } catch { /* ignore, traité comme indisponible */ }
        }
        if (ok) available.add(p.id);
      }
      if (!cancelled) { setAvailableIds(available); setChecking(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFiltered, units, checkIn, checkOut]);

  const visible = availableIds ? baseFiltered.filter((p) => availableIds.has(p.id)) : baseFiltered;
  const nights = nightsBetween(checkIn, checkOut);

  const resetFilters = () => {
    setQuery(''); setType(''); setGuests(''); setCheckIn(''); setCheckOut('');
  };

  if (!isTenant || !tenant) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-hotel-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Hébergements</h1>
        <p className="text-sm text-foreground-500 max-w-md">
          Retrouvez nos villas, appartements, riads et chambres directement sur la boutique du marchand.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
            Nos <span className="text-primary-500">hébergements</span>
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            Villas, appartements, riads, maisons d&apos;hôtes et chambres — réservez en direct, disponibilités en temps réel.
          </p>
        </div>
      </section>

      {/* Barre de recherche */}
      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto -mt-2 mb-8">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-3 md:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-[11px] font-semibold text-foreground-500 mb-1">Où ? (ville, nom…)</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Marrakech, Riad…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-500 mb-1">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer">
                <option value="">Tous les types</option>
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>{t.isChild ? '\u00a0\u00a0\u2014 ' : ''}{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-500 mb-1">Voyageurs</label>
              <input type="number" min={1} value={guests} onChange={(e) => setGuests(e.target.value)} placeholder="2"
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300" />
            </div>
            <div className="grid grid-cols-2 gap-2 lg:col-span-1">
              <div>
                <label className="block text-[11px] font-semibold text-foreground-500 mb-1">Arrivée</label>
                <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full px-2 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-950 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-foreground-500 mb-1">Départ</label>
                <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full px-2 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-950 focus:outline-none focus:border-primary-300" />
              </div>
            </div>
          </div>
          {/* Filtre rapide par catégorie */}
          {typeOptions.length > 0 && (
            <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1">
              <span className="text-[11px] font-semibold text-foreground-500 whitespace-nowrap flex items-center gap-1">
                <i className="ri-price-tag-3-line"></i>Catégorie
              </span>
              <button
                onClick={() => setType('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
                  type === ''
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'
                }`}
              >
                Tous
              </button>
              {typeOptions.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setType(t.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
                    type === t.value
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  <i className={t.icon}></i>{t.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <span className="text-xs text-foreground-500">
              {checking ? (
                <><i className="ri-loader-4-line animate-spin mr-1"></i>Vérification des disponibilités…</>
              ) : availableIds ? (
                `${visible.length} hébergement(s) disponible(s)${nights > 0 ? ` pour ${nights} nuit(s)` : ''}`
              ) : (
                `${visible.length} hébergement(s)`
              )}
            </span>
            <button onClick={resetFilters}
              className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line"></i>Réinitialiser
            </button>
          </div>
        </div>
      </section>

      {/* Liste */}
      <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
            <p className="text-foreground-600 mb-3 text-sm">{error}</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-hotel-line text-2xl text-foreground-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucun hébergement trouvé</h3>
            <p className="text-sm text-foreground-500">Essayez de modifier vos critères de recherche (dates, ville, type…).</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {visible.map((p) => {
              const meta = categoryMeta(categories, p.type);
              const mode = bookingModeMeta(p.booking_mode);
              const cover = coverByProp[p.id] || p.cover_image || '';
              const propUnits = units.filter((u) => u.property_id === p.id);
              const minPrice = propUnits.length > 0 && p.booking_mode !== 'ENTIRE_PROPERTY'
                ? Math.min(Number(p.price) || Infinity, ...propUnits.map((u) => Number(u.price) || Infinity))
                : Number(p.price) || 0;
              const displayPrice = Number.isFinite(minPrice) ? minPrice : Number(p.price) || 0;
              return (
                <Link
                  key={p.id}
                  to={`/hebergement/${p.id}`}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 group no-underline flex flex-col"
                >
                  <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
                    {cover ? (
                      <img src={cover} alt={p.name} title={`${p.name} — hébergement`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-foreground-300">
                        <i className="ri-image-line text-4xl"></i>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-50/90 text-foreground-700 backdrop-blur-sm flex items-center gap-1">
                        <i className={meta.icon}></i>{meta.label}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">{p.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1 truncate">
                      <i className="ri-map-pin-line mr-1"></i>{[p.city, p.country].filter(Boolean).join(', ') || 'Localisation non renseignée'}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-foreground-600">
                      <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-user-line"></i>{p.capacity}</span>
                      <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-hotel-bed-line"></i>{p.bed_count}</span>
                      <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-drop-line"></i>{p.bathroom_count}</span>
                    </div>
                    <div className="mt-auto pt-4 flex items-end justify-between">
                      <div className="text-base font-bold font-heading text-primary-600">
                        {displayPrice.toLocaleString()} <span className="text-xs font-normal text-foreground-400">{currency} / nuit</span>
                      </div>
                      <span className="text-[10px] text-foreground-400">
                        {p.booking_mode === 'ENTIRE_PROPERTY' ? 'Bien entier' : p.booking_mode === 'UNITS_ONLY' ? 'Chambre par chambre' : 'Les deux'}
                      </span>
                    </div>
                    <span className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium bg-primary-500 text-background-50 group-hover:bg-primary-600 transition-colors">
                      <i className="ri-calendar-check-line"></i>Voir &amp; réserver
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}